import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";
import {
  CreatePackagePresetDto,
  GetPackagePresetsResponseDto,
  PackagePresetDto,
  UpdatePackagePresetDto,
} from "@/types/api/api-types";

//#region Get

const getAllPackagePresets = async () => {
  const response = await axiosApi.get<GetPackagePresetsResponseDto>("v1/package-preset");
  return response.data;
};

export const useGetAllPackagePresets = () => {
  return useQuery({
    queryKey: ["packagePreset", "all"],
    queryFn: getAllPackagePresets,
  });
};

//#endregion

//#region Post

const createPackagePreset = async (payload: CreatePackagePresetDto) => {
  const response = await axiosApi.post<PackagePresetDto>("v1/package-preset", payload);
  return response.data;
};

export const useCreatePackagePreset = () => {
  const { t } = useTranslation("boExpeditionForm");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPackagePreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packagePreset", "all"] });
      toast.success(t("packagePresetApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("packagePresetApi.createError"));
    },
  });
};

//#endregion

//#region Patch

const updatePackagePreset = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdatePackagePresetDto;
}) => {
  const response = await axiosApi.patch<PackagePresetDto>(`v1/package-preset/${id}`, payload);
  return response.data;
};

export const useUpdatePackagePreset = () => {
  const { t } = useTranslation("boExpeditionForm");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePackagePreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packagePreset", "all"] });
      toast.success(t("packagePresetApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("packagePresetApi.updateError"));
    },
  });
};

//#endregion

//#region Delete

const deletePackagePreset = async (id: string) => {
  return axiosApi.delete(`v1/package-preset/${id}`);
};

export const useDeletePackagePreset = () => {
  const { t } = useTranslation("boExpeditionForm");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePackagePreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packagePreset", "all"] });
      toast.success(t("packagePresetApi.deleteSuccess"));
    },
    onError: () => {
      toast.error(t("packagePresetApi.deleteError"));
    },
  });
};

//#endregion
